import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Button,
  Tooltip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import DrawIcon from '@mui/icons-material/Draw';
import axiosInstance from '../../utils/axios';
import { Commission, User } from '../../types';
import BranchSelector from '../branches/BranchSelector';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SignatureCanvas from 'react-signature-canvas';

const CommissionReports: React.FC = () => {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [branchId, setBranchId] = useState('');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [tasksWithoutExpenses, setTasksWithoutExpenses] = useState<any[]>([]);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Firma
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerLastName, setSignerLastName] = useState('');
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const pendingPDFAction = useRef<'general' | 'byUser' | 'separate' | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string>('');
  const [includeExpenses, setIncludeExpenses] = useState(true);

  // Agrupación por usuario (calculada desde commissions)
  const commissionsByUser = useMemo(() => {
    const map = new Map<string, { user: any; commissions: Commission[]; total: number }>();
    commissions.forEach((c) => {
      const key = c.userId;
      if (!map.has(key)) {
        map.set(key, {
          user: c.user,
          commissions: [],
          total: 0,
        });
      }
      const entry = map.get(key)!;
      entry.commissions.push(c);
      entry.total += c.amount;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [commissions]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        setLogoDataUrl(canvas.toDataURL('image/png'));
      }
    };
    img.src = '/logo.png';
  }, []);

  useEffect(() => {
    fetchCommissions();
  }, [startDate, endDate, branchId, userId]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (branchId) params.append('branchId', branchId);
      if (userId) params.append('userId', userId);

      const response = await axiosInstance.get(`/commissions/report?${params.toString()}`);
      setCommissions(response.data.commissions || []);
      setSummary(response.data.summary || null);
      setTasksWithoutExpenses(response.data.tasksWithoutExpenses || []);
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Error al cargar comisiones');
      setCommissions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const addLogoToPDF = (doc: jsPDF) => {
    if (!logoDataUrl) return;
    const pageW = doc.internal.pageSize.width;
    doc.addImage(logoDataUrl, 'PNG', pageW - 69, 4, 55, 16);
  };

  const appendSignatureToPDF = (doc: jsPDF, sigDataUrl: string, fullName: string) => {
    const pageH = doc.internal.pageSize.height;
    const sigW = 70;
    const sigH = 25;
    const marginX = 14;
    const baseY = pageH - 32;

    doc.setDrawColor(180, 180, 180);
    doc.line(marginX, baseY, marginX + sigW + 10, baseY);
    if (sigDataUrl) {
      doc.addImage(sigDataUrl, 'PNG', marginX, baseY - sigH, sigW, sigH);
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(fullName, marginX, baseY + 5);
    doc.text('Firma / Aclaracion', marginX, baseY + 10);
    doc.setTextColor(0, 0, 0);
  };

  const openSigDialog = (action: 'general' | 'byUser' | 'separate') => {
    pendingPDFAction.current = action;
    setSigDialogOpen(true);
  };

  const handleSigConfirm = () => {
    const sigDataUrl = sigCanvasRef.current && !sigCanvasRef.current.isEmpty()
      ? sigCanvasRef.current.toDataURL('image/png')
      : '';
    const fullName = (signerName + ' ' + signerLastName).trim();
    setSigDialogOpen(false);
    if (pendingPDFAction.current === 'general') generatePDF(sigDataUrl, fullName, includeExpenses);
    else if (pendingPDFAction.current === 'byUser') generatePDFByUser(sigDataUrl, fullName, includeExpenses);
    else if (pendingPDFAction.current === 'separate') generatePDFSeparateByUser(sigDataUrl, fullName, includeExpenses);
    pendingPDFAction.current = null;
  };

  const generatePDF = (sigDataUrl: string, fullName: string, withExpenses: boolean) => {
    const doc = new jsPDF({ orientation: 'landscape' });

    addLogoToPDF(doc);

    // Título
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Reporte de Comisiones', 14, 18);

    // Subtítulo con filtros aplicados
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines: string[] = [];
    if (startDate || endDate) {
      lines.push(`Período: ${startDate || 'â€”'} al ${endDate || 'â€”'}`);
    }
    if (branchId && summary?.byBranch) {
      const branch = summary.byBranch.find((b: any) => b.branchId === branchId);
      if (branch) lines.push(`Sucursal: ${branch.branchName}`);
    }
    if (userId) {
      const u = users.find((us) => us.id === userId);
      if (u) lines.push(`Usuario: ${u.firstName} ${u.lastName}`);
    }
    lines.push(`Generado: ${new Date().toLocaleDateString('es-AR')}`);
    doc.text(lines.join('   |   '), 14, 26);

    // Tabla detalle
    const expCols = withExpenses
      ? ['Gasto Empl.', 'Gasto Ins.', 'Gasto Maq.', 'Total Gastos']
      : [];
    const expColStyles = withExpenses
      ? { 6: { halign: 'right' as const }, 7: { halign: 'right' as const }, 8: { halign: 'right' as const }, 9: { halign: 'right' as const }, 10: { halign: 'right' as const }, 11: { halign: 'center' as const }, 12: { halign: 'right' as const, fontStyle: 'bold' as const } }
      : { 6: { halign: 'right' as const }, 7: { halign: 'center' as const }, 8: { halign: 'right' as const, fontStyle: 'bold' as const } };

    const bodyRows = commissions.map((cc) => {
      const gEmp = (cc.task as any)?.gastoEmpleados ?? 0;
      const gIns = (cc.task as any)?.gastoInsumos ?? 0;
      const gMaq = (cc.task as any)?.gastoMaquinas ?? 0;
      const base = [
        new Date(cc.createdAt).toLocaleDateString('es-AR'),
        cc.user ? `${cc.user.firstName} ${cc.user.lastName}` : '-',
        cc.branch?.name || '-',
        cc.taskId ? 'Tarea' : 'Servicio',
        (cc.task as any)?.location || (cc.scheduledService as any)?.location || '-',
        (cc.task as any)?.category?.name || '-',
      ];
      const expData = withExpenses
        ? [formatCurrency(gEmp), formatCurrency(gIns), formatCurrency(gMaq), formatCurrency(gEmp+gIns+gMaq)]
        : [];
      return [...base, ...expData, formatCurrency(cc.baseAmount), `${cc.rate}%`, formatCurrency(cc.amount)];
    });
    const headCols = ['Fecha', 'Usuario', 'Sucursal', 'Tipo', 'Detalle', 'Categoria', ...expCols, 'Monto Base', 'Tasa', 'Comision'];

    autoTable(doc, {
      startY: 32,
      head: [headCols],
      body: bodyRows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 247, 255] },
      columnStyles: expColStyles as any,
    });

    // Totales finales
    const finalY: number = (doc as any).lastAutoTable?.finalY ?? 32;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total comisiones (${commissions.length} registros):`, 14, finalY + 10);
    doc.setTextColor(25, 118, 210);
    doc.text(formatCurrency(summary?.total || 0), doc.internal.pageSize.width - 14, finalY + 10, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    if (summary?.byUser?.length > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Por usuario:', 14, finalY + 18);
      doc.setFont('helvetica', 'normal');
      summary.byUser.forEach((item: any, idx: number) => {
        doc.text(`${item.userName}: ${formatCurrency(item.total)}`, 20, finalY + 24 + idx * 6);
      });
    }

    if (fullName) appendSignatureToPDF(doc, sigDataUrl, fullName);

    const fileParts = ['comisiones'];
    if (startDate) fileParts.push(startDate);
    if (endDate) fileParts.push(endDate);
    doc.save(`${fileParts.join('_')}.pdf`);
  };

  const generatePDFByUser = (sigDataUrl: string, fullName: string, withExpenses: boolean) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    let currentY = 18;

    addLogoToPDF(doc);

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Comisiones por Usuario', 14, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const periodLabel =
      startDate || endDate ? `Período: ${startDate || 'â€”'} al ${endDate || 'â€”'}   |   ` : '';
    doc.text(`${periodLabel}Generado: ${new Date().toLocaleDateString('es-AR')}`, 14, currentY);
    currentY += 8;

    commissionsByUser.forEach((entry) => {
      const userName = entry.user
        ? `${entry.user.firstName} ${entry.user.lastName}`
        : 'Sin usuario';

      // Encabezado del usuario
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(25, 118, 210);
      doc.setTextColor(255, 255, 255);
      doc.rect(14, currentY - 4, doc.internal.pageSize.width - 28, 8, 'F');
      doc.text(
        `${userName}   â€”   Total: ${formatCurrency(entry.total)}   (${entry.commissions.length} comisiones)`,
        16,
        currentY + 0.5
      );
      doc.setTextColor(0, 0, 0);
      currentY += 8;

      const expColsByUser = withExpenses
        ? { 5: { halign: 'right' as const }, 6: { halign: 'right' as const }, 7: { halign: 'right' as const }, 8: { halign: 'right' as const }, 9: { halign: 'right' as const }, 10: { halign: 'center' as const }, 11: { halign: 'right' as const, fontStyle: 'bold' as const } }
        : { 5: { halign: 'right' as const }, 6: { halign: 'center' as const }, 7: { halign: 'right' as const, fontStyle: 'bold' as const } };
      const headByUser = withExpenses
        ? ['Fecha', 'Sucursal', 'Tipo', 'Detalle', 'Categoria', 'G.Empl', 'G.Ins', 'G.Maq', 'T.Gastos', 'Monto Base', 'Tasa', 'Comision']
        : ['Fecha', 'Sucursal', 'Tipo', 'Detalle', 'Categoria', 'Monto Base', 'Tasa', 'Comision'];

      const rows = entry.commissions.map((cc) => {
        const gEmp = (cc.task as any)?.gastoEmpleados ?? 0;
        const gIns = (cc.task as any)?.gastoInsumos ?? 0;
        const gMaq = (cc.task as any)?.gastoMaquinas ?? 0;
        const base = [
          new Date(cc.createdAt).toLocaleDateString('es-AR'),
          cc.branch?.name || '-',
          cc.taskId ? 'Tarea' : 'Servicio',
          (cc.task as any)?.location || (cc.scheduledService as any)?.location || '-',
          (cc.task as any)?.category?.name || '-',
        ];
        const expData = withExpenses
          ? [formatCurrency(gEmp), formatCurrency(gIns), formatCurrency(gMaq), formatCurrency(gEmp+gIns+gMaq)]
          : [];
        return [...base, ...expData, formatCurrency(cc.baseAmount), `${cc.rate}%`, formatCurrency(cc.amount)];
      });

      autoTable(doc, {
        startY: currentY,
        head: [headByUser],
        body: rows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [224, 235, 255], textColor: 0, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 255] },
        columnStyles: expColsByUser as any,
        margin: { left: 14, right: 14 },
      });

      currentY = ((doc as any).lastAutoTable?.finalY ?? currentY) + 10;

      // Nueva página si no hay espacio
      if (currentY > doc.internal.pageSize.height - 30) {
        doc.addPage();
        currentY = 18;
      }
    });

    if (fullName) appendSignatureToPDF(doc, sigDataUrl, fullName);

    const fileParts = ['comisiones_por_usuario'];
    if (startDate) fileParts.push(startDate);
    if (endDate) fileParts.push(endDate);
    doc.save(`${fileParts.join('_')}.pdf`);
  };

  const generatePDFSeparateByUser = (sigDataUrl: string, fullName: string, withExpenses: boolean) => {
    commissionsByUser.forEach((entry) => {
      const userName = entry.user
        ? `${entry.user.firstName} ${entry.user.lastName}`
        : 'Sin usuario';

      const doc = new jsPDF({ orientation: 'landscape' });
      addLogoToPDF(doc);

      // Título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`Comisiones - ${userName}`, 14, 18);

      // Subtítulo
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const periodLabel =
        startDate || endDate ? `Período: ${startDate || '—'} al ${endDate || '—'}   |   ` : '';
      doc.text(
        `${periodLabel}Generado: ${new Date().toLocaleDateString('es-AR')}   |   ${entry.commissions.length} comisiones`,
        14,
        26
      );

      const expColsSep = withExpenses
        ? { 5: { halign: 'right' as const }, 6: { halign: 'right' as const }, 7: { halign: 'right' as const }, 8: { halign: 'right' as const }, 9: { halign: 'right' as const }, 10: { halign: 'center' as const }, 11: { halign: 'right' as const, fontStyle: 'bold' as const } }
        : { 5: { halign: 'right' as const }, 6: { halign: 'center' as const }, 7: { halign: 'right' as const, fontStyle: 'bold' as const } };
      const headSep = withExpenses
        ? ['Fecha', 'Sucursal', 'Tipo', 'Detalle', 'Categoria', 'G.Empl', 'G.Ins', 'G.Maq', 'T.Gastos', 'Monto Base', 'Tasa', 'Comision']
        : ['Fecha', 'Sucursal', 'Tipo', 'Detalle', 'Categoria', 'Monto Base', 'Tasa', 'Comision'];

      const rows = entry.commissions.map((cc) => {
        const gEmp = (cc.task as any)?.gastoEmpleados ?? 0;
        const gIns = (cc.task as any)?.gastoInsumos ?? 0;
        const gMaq = (cc.task as any)?.gastoMaquinas ?? 0;
        const base = [
          new Date(cc.createdAt).toLocaleDateString('es-AR'),
          cc.branch?.name || '-',
          cc.taskId ? 'Tarea' : 'Servicio',
          (cc.task as any)?.location || (cc.scheduledService as any)?.location || '-',
          (cc.task as any)?.category?.name || '-',
        ];
        const expData = withExpenses
          ? [formatCurrency(gEmp), formatCurrency(gIns), formatCurrency(gMaq), formatCurrency(gEmp+gIns+gMaq)]
          : [];
        return [...base, ...expData, formatCurrency(cc.baseAmount), `${cc.rate}%`, formatCurrency(cc.amount)];
      });

      autoTable(doc, {
        startY: 32,
        head: [headSep],
        body: rows,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 247, 255] },
        columnStyles: expColsSep as any,
        margin: { left: 14, right: 14 },
      });

      // Fila total
      const finalY: number = (doc as any).lastAutoTable?.finalY ?? 32;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Total comisiones:', 14, finalY + 10);
      doc.setTextColor(25, 118, 210);
      doc.text(formatCurrency(entry.total), doc.internal.pageSize.width - 14, finalY + 10, { align: 'right' });
      doc.setTextColor(0, 0, 0);

      if (fullName) appendSignatureToPDF(doc, sigDataUrl, fullName);

      const safeUserName = userName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const fileParts = [`comisiones_${safeUserName}`];
      if (startDate) fileParts.push(startDate);
      if (endDate) fileParts.push(endDate);
      doc.save(`${fileParts.join('_')}.pdf`);
    });
  };

  const handleRecalculate = async () => {
    if (!window.confirm('¿Recalcular TODAS las comisiones de tareas completadas aplicando los gastos actuales? Esto actualizará los registros existentes.')) return;
    try {
      setRecalculating(true);
      setRecalcMsg(null);
      const response = await axiosInstance.post('/commissions/recalculate');
      setRecalcMsg(response.data.message);
      fetchCommissions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al recalcular');
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Comisiones</Typography>
        <Box display="flex" gap={1}>
          {activeTab === 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => openSigDialog("general")}
              disabled={loading || commissions.length === 0}
            >
              Generar PDF
            </Button>
          )}
          {activeTab === 1 && (
            <>
              <Button
                variant="contained"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => openSigDialog("byUser")}
                disabled={loading || commissions.length === 0}
              >
                Generar PDF por usuario
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => openSigDialog("separate")}
                disabled={loading || commissions.length === 0}
              >
                Archivos separados por usuario
              </Button>
            </>
          )}
          <Tooltip title="Recalcula todas las comisiones descontando los gastos registrados en cada tarea">
            <Button
              variant="outlined"
              color="warning"
              startIcon={recalculating ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRecalculate}
              disabled={recalculating}
            >
              Recalcular comisiones
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {recalcMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRecalcMsg(null)}>
          {recalcMsg}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtros compartidos */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" mb={2}>Filtros</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Fecha desde"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Fecha hasta"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <BranchSelector
              value={branchId}
              onChange={setBranchId}
              showAll
              label="Sucursal"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Usuario</InputLabel>
              <Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                label="Usuario"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Reporte General" />
        <Tab label={`Por Usuario${commissions.length > 0 ? ` (${commissionsByUser.length})` : ''}`} />
      </Tabs>

      {/* â”€â”€ TAB 0: Reporte General â”€â”€ */}
      {activeTab === 0 && (
        <>
          {/* Resumen */}
          {summary && (
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Comisiones
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(summary.total || 0)}
                    </Typography>
                    <Typography color="textSecondary">
                      {commissions.length} comisiones
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Gastos (tareas con gastos)
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {formatCurrency(summary.totalGastos || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Neto (tareas con gastos)
                    </Typography>
                    <Typography variant="h4" color={summary.totalNeto >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(summary.totalNeto || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Por Usuario
                    </Typography>
                    {summary.byUser?.map((item: any) => (
                      <Box key={item.userId} display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{item.userName}</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(item.total)}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Por Sucursal
                    </Typography>
                    {summary.byBranch?.map((item: any) => (
                      <Box key={item.branchId} display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{item.branchName}</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(item.total)}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Advertencia: tareas sin gastos */}
          {tasksWithoutExpenses.length > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
              <Typography fontWeight="bold" gutterBottom>
                {tasksWithoutExpenses.length} tarea{tasksWithoutExpenses.length > 1 ? 's' : ''} sin gastos registrados:
              </Typography>
              <List dense disablePadding>
                {tasksWithoutExpenses.map((t: any) => (
                  <ListItem key={t.taskId} disablePadding>
                    <ListItemText
                      primary={`${t.location} - ${new Date(t.dateTime).toLocaleDateString('es-AR')} - ${formatCurrency(t.amount)}`}
                      secondary={t.user ? `Responsable: ${t.user.firstName} ${t.user.lastName}` : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {/* Tabla general */}
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell align="right">Monto Base</TableCell>
                    <TableCell align="right">Tasa (%)</TableCell>
                    <TableCell align="right">Comisión</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No se encontraron comisiones
                      </TableCell>
                    </TableRow>
                  ) : (
                    commissions.map((commission) => (
                      <TableRow key={commission.id}>
                        <TableCell>
                          {new Date(commission.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {commission.user
                            ? `${commission.user.firstName} ${commission.user.lastName}`
                            : '-'}
                        </TableCell>
                        <TableCell>{commission.branch?.name || '-'}</TableCell>
                        <TableCell>
                          {commission.taskId ? (
                            <Chip label="Tarea" size="small" color="primary" variant="outlined" />
                          ) : (
                            <Chip label="Servicio" size="small" color="secondary" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(commission.baseAmount)}</TableCell>
                        <TableCell align="right">{commission.rate}%</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={500}>{formatCurrency(commission.amount)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && commissions.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => openSigDialog("general")}
              >
                Descargar PDF ({commissions.length} registros)
              </Button>
            </Box>
          )}
        </>
      )}

      {/* â”€â”€ TAB 1: Por Usuario â”€â”€ */}
      {activeTab === 1 && (
        <>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : commissionsByUser.length === 0 ? (
            <Typography color="textSecondary" textAlign="center" mt={4}>
              No se encontraron comisiones
            </Typography>
          ) : (
            <>
              {commissionsByUser.map((entry) => {
                const userName = entry.user
                  ? `${entry.user.firstName} ${entry.user.lastName}`
                  : 'Sin usuario';
                const initials = entry.user
                  ? `${entry.user.firstName?.[0] ?? ''}${entry.user.lastName?.[0] ?? ''}`
                  : '?';
                return (
                  <Accordion key={entry.user?.id ?? 'unknown'} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
                          {initials}
                        </Avatar>
                        <Box flexGrow={1}>
                          <Typography fontWeight={600}>{userName}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {entry.commissions.length} comisión{entry.commissions.length !== 1 ? 'es' : ''}
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mr: 2 }}>
                          {formatCurrency(entry.total)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell>Fecha</TableCell>
                              <TableCell>Sucursal</TableCell>
                              <TableCell>Tipo</TableCell>
                              <TableCell>Detalle</TableCell>
                              <TableCell>Categoría</TableCell>
                              <TableCell align="right">Monto Base</TableCell>
                              <TableCell align="right">Tasa</TableCell>
                              <TableCell align="right">Comisión</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {entry.commissions.map((c) => (
                              <TableRow key={c.id} hover>
                                <TableCell>{new Date(c.createdAt).toLocaleDateString('es-AR')}</TableCell>
                                <TableCell>{c.branch?.name || '-'}</TableCell>
                                <TableCell>
                                  {c.taskId ? (
                                    <Chip label="Tarea" size="small" color="primary" variant="outlined" />
                                  ) : (
                                    <Chip label="Servicio" size="small" color="secondary" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {(c.task as any)?.location || (c.scheduledService as any)?.location || '-'}
                                </TableCell>
                                <TableCell>{(c.task as any)?.category?.name || '-'}</TableCell>
                                <TableCell align="right">{formatCurrency(c.baseAmount)}</TableCell>
                                <TableCell align="right">{c.rate}%</TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={600} color="primary.main">
                                    {formatCurrency(c.amount)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Fila de total por usuario */}
                            <TableRow sx={{ bgcolor: 'primary.50' }}>
                              <TableCell colSpan={7} align="right">
                                <Typography fontWeight={700}>Total {userName}:</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700} color="primary.main">
                                  {formatCurrency(entry.total)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {/* Total global */}
              <Paper sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" fontWeight={700}>
                  Total general ({commissions.length} comisiones)
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(summary?.total || 0)}
                </Typography>
              </Paper>

              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => openSigDialog("separate")}
                >
                  Descargar archivos separados por usuario
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => openSigDialog("byUser")}
                >
                  Descargar PDF por usuario (un archivo)
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {/* Dialog de firma */}
      <Dialog open={sigDialogOpen} onClose={() => setSigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DrawIcon color="primary" />
          Firma del responsable
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2, mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nombre"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Ej: Juan"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Apellido"
                value={signerLastName}
                onChange={(e) => setSignerLastName(e.target.value)}
                placeholder="Ej: Perez"
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeExpenses}
                onChange={(e) => setIncludeExpenses(e.target.checked)}
                color="primary"
              />
            }
            label="Incluir detalle de gastos (G.Empleados, G.Insumos, G.Maquinas)"
          />
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" mb={1}>
            Firma (dibuje con el mouse o dedo):
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: '#fafafa',
            }}
          >
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="black"
              canvasProps={{ width: 520, height: 160, style: { display: 'block' } }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button size="small" onClick={() => sigCanvasRef.current?.clear()}>
              Limpiar firma
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSigDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleSigConfirm}
            disabled={!signerName && !signerLastName}
          >
            Generar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionReports;


